import React, { useState } from 'react';
import { 
  Box, CssBaseline, Drawer, List, ListItem, ListItemButton, 
  ListItemIcon, ListItemText, Typography, Avatar, IconButton, 
  Stack, Button, Chip, Paper, Grid, AppBar, Toolbar, useMediaQuery, 
  useTheme, Tooltip, Zoom
} from '@mui/material';
import {
  Dashboard, Description, EventNote, Group, Search, 
  Menu as MenuIcon, ChevronLeft, ChevronRight, FilterList, Add
} from '@mui/icons-material';

const drawerWidth = 240;
const collapsedWidth = 80;

const ExamCard = ({ color, title, time, room, code }) => (
  <Paper elevation={0} sx={{ 
    p: 2, borderRadius: 3, bgcolor: color, minWidth: { xs: '100%', sm: 200 }, 
    border: '1px solid rgba(0,0,0,0.05)' 
  }}>
    <Stack direction="row" justifyContent="space-between" mb={1}>
      <Typography variant="caption" fontWeight="bold">{code}</Typography>
      <Typography variant="caption" color="text.secondary">{time}</Typography>
    </Stack>
    <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
    <Stack direction="row" alignItems="center" mt={1} spacing={1}>
      <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>M</Avatar>
      <Typography variant="caption">{room}</Typography>
    </Stack>
    <Stack direction="row" mt={2} spacing={1}>
      <Chip label="Confirmed" size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'white' }} />
      <Chip label="12" size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'white' }} />
    </Stack>
  </Paper>
);

export default function TestDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Overview', icon: <Dashboard /> },
    { text: 'Class Preparation', icon: <Description /> },
    { text: 'Exams', icon: <EventNote />, active: true },
    { text: 'Students', icon: <Group /> },
  ];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#F8F9FA' }}>
      <Box sx={{ p: 3, fontWeight: 'bold', fontSize: '1.2rem', textAlign: isTablet && !mobileOpen ? 'center' : 'left' }}>
        {isTablet && !mobileOpen ? 'L' : 'Logo'}
      </Box>
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            {/* Tooltip added here for the collapsed state */}
            <Tooltip 
              title={isTablet && !mobileOpen ? item.text : ""} 
              placement="right" 
              TransitionComponent={Zoom}
              arrow
            >
              <ListItemButton 
                selected={item.active} 
                sx={{ 
                  borderRadius: 2, 
                  justifyContent: isTablet && !mobileOpen ? 'center' : 'flex-start',
                  px: isTablet && !mobileOpen ? 0 : 2
                }}
              >
                <ListItemIcon sx={{ minWidth: isTablet && !mobileOpen ? 0 : 40, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {(!isTablet || mobileOpen) && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent={isTablet && !mobileOpen ? 'center' : 'flex-start'}>
          <Tooltip title={isTablet && !mobileOpen ? "Amitkumar (Teacher)" : ""} placement="right" arrow>
            <Avatar src="/avatar.jpg" />
          </Tooltip>
          {(!isTablet || mobileOpen) && (
            <Box>
              <Typography variant="body2" fontWeight="bold">Amitkumar</Typography>
              <Typography variant="caption" color="text.secondary">Teacher</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', bgcolor: '#F8F9FA', minHeight: '100vh' }}>
      <CssBaseline />

      {isMobile && (
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'black', borderBottom: '1px solid #eee' }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>Exams</Typography>
            <Avatar src="/avatar.jpg" sx={{ width: 32, height: 32 }} />
          </Toolbar>
        </AppBar>
      )}

      <Box component="nav">
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: 'none' } }}
          >
            {drawerContent}
          </Drawer>
        ) : (
          <Box sx={{ 
            width: isTablet ? collapsedWidth : drawerWidth, 
            height: '100vh', 
            position: 'sticky', 
            top: 0,
            transition: 'width 0.3s' 
          }}>
            {drawerContent}
          </Box>
        )}
      </Box>

      <Box component="main" sx={{ 
        flexGrow: 1, 
        p: { xs: 2, sm: 3 }, 
        bgcolor: 'white', 
        m: { xs: 0, sm: 2 }, 
        borderRadius: { xs: 0, sm: 5 },
        width: '100%',
        overflowX: 'hidden'
      }}>
        {!isMobile && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Typography variant="h4" fontWeight="bold">Exams</Typography>
              <Typography variant="body2" color="text.secondary">
                Track student attendance and monitor absences easily.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <IconButton><Search /></IconButton>
              <Button variant="contained" startIcon={<Add />} sx={{ borderRadius: 2, bgcolor: 'black' }}>
                Add new exam
              </Button>
            </Stack>
          </Stack>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Exam Calendar</Typography>
              <Button variant="outlined" size="small" startIcon={<FilterList />} sx={{ borderRadius: 2 }}>Filter</Button>
            </Stack>
            
            <Stack direction="row" spacing={1} mb={3} sx={{ overflowX: 'auto', pb: 1 }}>
              {['Jan', 'Feb', 'March', 'April', 'May'].map((month) => (
                <Chip 
                  key={month} 
                  label={month} 
                  variant={month === 'Feb' ? 'filled' : 'outlined'}
                  sx={{ px: 2, bgcolor: month === 'Feb' ? 'black' : 'transparent', color: month === 'Feb' ? 'white' : 'black' }}
                />
              ))}
            </Stack>

            <Stack spacing={2}>
              {[1, 2, 3].map((row) => (
                <Stack key={row} direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                  <Typography variant="body2" sx={{ width: 30, pt: { sm: 2 } }}>{row}</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexGrow: 1 }}>
                    {row === 1 && (
                      <>
                        <ExamCard color="#E8EDF9" title="Ceres 203" time="8:00 am" room="Main Room" code="383" />
                        <ExamCard color="#FFF4E5" title="Class 303" time="11:00 am" room="Physics Room" code="393" />
                      </>
                    )}
                    {row === 3 && (
                      <>
                        <ExamCard color="#FDE8E8" title="Class 303" time="8:00 am" room="Main Room" code="303" />
                        <ExamCard color="#E8F5E9" title="Class 303" time="1:00 pm" room="Physics Room" code="283" />
                      </>
                    )}
                    {row === 2 && <Typography variant="caption" sx={{ color: '#ccc', py: 1 }}>No exam scheduled</Typography>}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight="bold">February</Typography>
                <Stack direction="row">
                  <IconButton size="small"><ChevronLeft /></IconButton>
                  <IconButton size="small"><ChevronRight /></IconButton>
                </Stack>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, textAlign: 'center' }}>
                {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
                   <Typography key={d} variant="caption" fontWeight="bold">{d}</Typography>
                ))}
                {[...Array(28)].map((_, i) => (
                  <Typography key={i} variant="caption" sx={{ 
                    p: 1, bgcolor: i === 7 ? 'black' : 'transparent', color: i === 7 ? 'white' : 'black', borderRadius: '50%'
                  }}>
                    {i + 1}
                  </Typography>
                ))}
              </Box>
            </Paper>

            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Upcoming exams</Typography>
            <Stack spacing={2}>
              <Paper sx={{ p: 2, bgcolor: '#F3E5F5', borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight="bold">Math Exam</Typography>
                  <Chip label="5 Days left" size="small" />
                </Stack>
                <Typography variant="caption">19 Feb • 7:30 am</Typography>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}